interface ScalingFactor {
  minDepth: number
  maxDepth: number
  factor: number
}

export class WellBoreVisualisation {
  xmlns: string = 'http://www.w3.org/2000/svg'
  boreWidth: number = 130
  leftOfBoreOffset: number = 170
  totalVisualisationWidth: number = 300
  boreBottomLineOffset: number = 130
  boreBottomTextPadding: number = 40
  leftOfBoreLineOffset: number = 100
  accruedDescriptionLabelYOrd: number = 0
  accruedDepthLabelYOrd: number = 0
  minLabelHeight: number = 15
  bottomOfBorePadding: number = 100
  boreScaleLeftOffset: number = 50
  boreScaleLeftTextOffset: number = 50
  boreScaleWidth: number = 5
  verticallyCenteredTextOffset: number = 4
  screenWidth: number = 20
  containerName: string
  configuration: any
  loadedElements: string[] = []
  exportMargin: number = 50
  exportHeaderHeight: number = 155
  exportHeaderTopMargin: number = 10
  staticWaterLevelIndicatorHeight: number = 8
  stratumDescriptionWidth: number = 270
  verticalScalingFactor: number = 0
  // Vertical scaling factors for various well depths
  scalingFactors: ScalingFactor[] = [
    {
      minDepth: 0,
      maxDepth: 50,
      factor: 800,
    },
    {
      minDepth: 50,
      maxDepth: 300,
      factor: 1200,
    },
    {
      minDepth: 300,
      maxDepth: 500,
      factor: 1800,
    },
    {
      minDepth: 500,
      maxDepth: 1000,
      factor: 2500,
    },
    {
      minDepth: 1000,
      maxDepth: 1500,
      factor: 3000,
    },
    {
      minDepth: 1500,
      maxDepth: 2000,
      factor: 4000,
    },
    {
      minDepth: 2000,
      maxDepth: 2500,
      factor: 5000,
    },
    {
      minDepth: 2500,
      maxDepth: 3000,
      factor: 6000,
    },
    {
      minDepth: 3000,
      maxDepth: 5000,
      factor: 10000,
    },
    {
      minDepth: 5000,
      maxDepth: 10000,
      factor: 20000,
    },
    {
      minDepth: 10000,
      maxDepth: 20000,
      factor: 40000,
    },
  ]

  constructor(containerName: string) {
    this.containerName = containerName
  }

  isError: boolean = false

  // Determine how to scale into pixel heights, based on the well depth
  getVerticalScalingFactor() {
    // Abort rendering.
    if (!this.configuration.header.maxDepthDrilled) {
      this.isError = true
      return -1
    }

    if (this.verticalScalingFactor !== 0) {
      return this.verticalScalingFactor
    }

    let factor = 3

    this.scalingFactors.forEach((scalingFactor) => {
      if (
        this.configuration.header.maxDepthDrilled >= scalingFactor.minDepth &&
        this.configuration.header.maxDepthDrilled <= scalingFactor.maxDepth
      ) {
        factor = scalingFactor.factor / this.configuration.header.maxDepthDrilled
      }
    })

    this.verticalScalingFactor = factor
    return factor
  }

  setJson(json: any) {
    if (json != null) {
      this.configuration = json
    }
  }

  buildReferenceElements() {
    let assets = this.configuration.assets
    for (const key in assets) {
      let div = document.createElement('div')
      div.setAttribute('hidden', 'true')
      div.id = key
      div.innerHTML = assets[key]
      document.getElementById('assets')!.appendChild(div)
    }
  }

  convertSVGToPNG(serializedSVG: any, height: number, width: number, margin: number): Promise<string> {
    return new Promise(function (resolve, reject) {
      try {
        const domUrl = window.URL || window.webkitURL || window
        if (!domUrl) {
          throw new Error('Conversion from SVG not supported')
        }

        if (!serializedSVG.match(/xmlns="/im)) {
          serializedSVG = serializedSVG.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ')
        }

        const canvas = document.createElement('canvas')
        canvas.width = width + margin
        canvas.height = height + margin

        const ctx = canvas.getContext('2d')
        ctx!.beginPath()
        ctx!.rect(0, 0, canvas.width, canvas.height)
        ctx!.fillStyle = '#ffffff'
        ctx!.fill()

        const img = new Image()

        img.onload = function (event) {
          ctx!.drawImage(img, Math.floor(margin / 2), Math.floor(margin / 2))
          resolve(canvas.toDataURL())
        }

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(serializedSVG)))
      } catch (err) {
        reject('Unable to generate PNG from SVG ' + err)
      }
    })
  }

  escapeFilename(filename: string) {
    return filename.replace(/[ &/\\#,+()$~%.'":*?<>{}]/g, '')
  }

  getFilename() {
    if (this.configuration.header.wellName != null && this.configuration.header.councilWellNo != null) {
      return this.escapeFilename(`${this.configuration.header.councilWellNo}_${this.configuration.header.wellName}`)
    } else {
      return 'bore_visualisation'
    }
  }

  downloadImage(name: string, url: string, type: string) {
    var xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    xhr.onload = function () {
      var urlCreator = window.URL || window.webkitURL
      var imageUrl = urlCreator.createObjectURL(this.response)
      var tag = document.createElement('a')
      tag.href = imageUrl
      tag.download = name
      document.body.appendChild(tag)
      tag.click()
      document.body.removeChild(tag)
    }
    xhr.send()
  }

  // Get an <svg> element
  getSVG(width: number, height: number, id?: string, x?: number, y?: number) {
    const svgElem: any = document.createElementNS(this.xmlns, 'svg')
    svgElem.setAttribute('width', String(width))
    svgElem.setAttribute('height', String(height))
    if (id != null) {
      svgElem.setAttribute('id', id)
    }
    if (x != null) {
      svgElem.setAttribute('x', String(x))
    }
    if (y != null) {
      svgElem.setAttribute('y', String(y))
    }
    return svgElem
  }

  renderHeader(svgParent: any) {
    const headerSVGElement: any = this.getSVG(
      this.boreWidth + this.leftOfBoreOffset + this.totalVisualisationWidth,
      this.exportHeaderHeight,
      'header_svg',
    )

    const getTSpan = (label: string, value: string | null) => {
      return value == null
        ? '<tspan style= "font-weight:bold" dy="15" x="10">' + label + '</tspan><tspan x="160">: - </tspan>'
        : '<tspan style= "font-weight:bold" dy="15" x="10">' + label + '</tspan><tspan x="160">: ' + value + '</tspan>'
    }

    const textElement = this.getText(0, this.exportHeaderTopMargin, '', 15, 'Helvetica')
    textElement.innerHTML =
      getTSpan('Council well number', this.configuration.header.councilWellNo) +
      getTSpan('Well name', this.configuration.header.wellName) +
      getTSpan('Drilling company', this.configuration.header.drillingCompanyName) +
      getTSpan('Drilling date', this.configuration.header.drillingDate) +
      getTSpan('Drilling method', this.configuration.header.drillingMethod) +
      getTSpan('Locality', this.configuration.header.wellLocality) +
      getTSpan('Total depth drilled', String(this.configuration.header.maxDepthDrilled) + 'm') +
      getTSpan(
        'NZTM',
        this.configuration.header.easting && this.configuration.header.northing
          ? `${this.configuration.header.easting} : ${this.configuration.header.northing}`
          : null,
      )

    headerSVGElement.appendChild(textElement)
    svgParent.appendChild(headerSVGElement)
    return headerSVGElement
  }

  removeHeader() {
    let header: any = document.getElementById('header_svg')
    let parent: any = header!.parentNode
    parent!.removeChild(header)
  }

  // Get and append a multiline text element
  appendMultilineText(
    parentSvg: any,
    x: number,
    y: number,
    width: number,
    title: string,
    fontSize: number = 13,
    fontFamily: string = 'helvetica',
  ) {
    const height: number = 250
    const words: string[] = title.split(' ')
    let line: string = ''
    let lineIndex: number = 0

    const boundingRect = this.getSVG(width, height, String(Math.random()), x, y)
    const textElement = this.getText(10, 12, '', fontSize, fontFamily)
    const testElementId: string = String(Math.random())

    boundingRect.appendChild(textElement)
    parentSvg.appendChild(boundingRect)

    textElement.innerHTML = '<tspan id="' + testElementId + '">.</tspan >'

    if (document.getElementById(testElementId) !== null) {
      for (var n = 0; n < words.length; n++) {
        var testLine = line + words[n] + ' '
        document.getElementById(testElementId)!.innerHTML = testLine
        let rectSizing = document.getElementById(testElementId)!.getBoundingClientRect()

        if (rectSizing.width > width && n > 0) {
          textElement.innerHTML += '<tspan x="0" dy="' + (lineIndex === 0 ? 0 : 15) + '">' + line + '</tspan>'
          lineIndex += 1
          line = words[n] + ' '
        } else {
          line = testLine
        }
      }

      textElement.innerHTML += '<tspan x="0" dy="' + (lineIndex === 0 ? 0 : 15) + '">' + line + '</tspan>'
      document.getElementById(testElementId)!.remove()
      return lineIndex + 1
    } else {
      return 1
    }
  }

  // return a <text> element
  getText(x: number, y: number, title: string, fontSize: number = 13, fontFamily: string = 'helvetica') {
    const text = document.createElementNS(this.xmlns, 'text')
    text.setAttribute('x', String(x))
    text.setAttribute('y', String(y))
    text.setAttribute('font-size', String(fontSize))
    text.setAttribute('font-family', fontFamily)
    let textNode = document.createTextNode(title)
    text.appendChild(textNode)
    return text
  }

  // return a <rect>
  getRect(x: number, y: number, width: number, height: number, style: string = 'stroke:black;stroke-width:1') {
    if (height < 0) {
      height = 0
    }
    const rect = document.createElementNS(this.xmlns, 'rect')
    rect.setAttribute('x', String(x))
    rect.setAttribute('y', String(y))
    rect.setAttribute('width', String(width))
    rect.setAttribute('height', String(height))
    rect.setAttribute('style', style)
    return rect
  }

  // return a <line>
  getLine(x1: number, y1: number, x2: number, y2: number, style: string = 'stroke:black;stroke-width:1') {
    const line = document.createElementNS(this.xmlns, 'line')
    line.setAttribute('x1', String(x1))
    line.setAttribute('y1', String(y1))
    line.setAttribute('x2', String(x2))
    line.setAttribute('y2', String(y2))
    line.setAttribute('style', style)
    return line
  }

  // Get the total Height of the visualisation in pixels
  getTotalHeight() {
    if (this.configuration.header.maxDepthDrilled > this.configuration.header.maxDepthLogged) {
      return this.configuration.header.maxDepthDrilled * this.getVerticalScalingFactor()
    } else {
      return this.configuration.header.maxDepthLogged * this.getVerticalScalingFactor()
    }
  }

  // Create a style element to inject into an SVG tile
  getSVGStyle(materialClass: string, color: string, water: boolean = false) {
    const styleElement = document.createElement('div')
    styleElement.innerHTML =
      '<style type="text/css"> .' +
      materialClass +
      ' polygon {fill:' +
      color +
      ';} .' +
      materialClass +
      ' circle {fill:' +
      color +
      ';}' +
      materialClass +
      ' path {fill:' +
      color +
      ';} .' +
      materialClass +
      ' path {fill:' +
      color +
      ';} .' +
      materialClass +
      ' path {stroke:' +
      color +
      ';} .' +
      materialClass +
      ' .water {fill:' +
      (water === false ? 'none' : '#0066ff') +
      ';} '
    return styleElement.firstChild
  }

  // Create all of the elements for a single stratum (may require multiple tiles)
  createElementsForStratum(
    stratumTop: number,
    stratumBottom: number,
    staticWaterLevel: number,
    parentSvg: any,
    materialId: string,
    materialName: string,
    materialHeight: number,
    topYOrd: number,
    color: string = '#000000',
    isWaterBearing: boolean = false,
    primacy: string,
  ) {
    if (primacy !== 'minor') {
      isWaterBearing = false
    }

    const materialClass: string = 'class_' + String(Math.random()).replace('.', '_')
    const materialElement: any = document.getElementById(materialId)

    // Abort rendering if the tiles have not been built (rather than blow up)
    if (materialElement == null) {
      return -1
    }

    const materialOriginalSvg: any = materialElement!.childNodes[2]
    const boxHeight: number = materialOriginalSvg.height.animVal.value

    // Abort rendering if no boxheight
    if (!boxHeight) {
      this.isError = true
      return -1
    }
    const material: any = materialOriginalSvg.childNodes[1]

    const loopsNeeded: number = Math.trunc(materialHeight / boxHeight) + 1
    let yOrd: number = topYOrd
    let numLines: number = 0

    // If the accrued description level is less than the start of this stratum, we bump it up
    if (this.accruedDescriptionLabelYOrd < yOrd) {
      this.accruedDescriptionLabelYOrd = yOrd
    }

    if (this.accruedDepthLabelYOrd < yOrd) {
      this.accruedDepthLabelYOrd = yOrd
    }

    if (primacy === 'major') {
      // render a static water level inverted triangle marker
      this.renderStaticWaterLevel(parentSvg, staticWaterLevel)

      // render the generated bore description
      numLines = this.renderTitle(parentSvg, yOrd, this.accruedDescriptionLabelYOrd, materialName)

      if (this.accruedDepthLabelYOrd !== this.exportHeaderHeight) {
        this.renderStratumBottomLine(parentSvg, yOrd, this.accruedDepthLabelYOrd, String(stratumTop))
      }

      // Calcuate the accrued position of the next description label
      if (this.accruedDescriptionLabelYOrd + this.minLabelHeight * numLines > yOrd) {
        this.accruedDescriptionLabelYOrd = this.accruedDescriptionLabelYOrd + this.minLabelHeight * numLines
      } else {
        this.accruedDescriptionLabelYOrd = yOrd + this.minLabelHeight * numLines
      }

      if (this.accruedDepthLabelYOrd + this.minLabelHeight > yOrd) {
        this.accruedDepthLabelYOrd = this.accruedDepthLabelYOrd + this.minLabelHeight
      } else {
        this.accruedDepthLabelYOrd = yOrd + this.minLabelHeight
      }

      // Extend the image size if the stratum description exceeds total height
      if (this.accruedDescriptionLabelYOrd > parentSvg.getAttribute('height')) {
        parentSvg.setAttribute('height', this.accruedDescriptionLabelYOrd)
      }
    }

    // Replicate the tiles as needed
    for (let i = 0; i < loopsNeeded; i++) {
      let materialElem = this.getSVG(
        this.boreWidth,
        i + 1 === loopsNeeded ? materialHeight - boxHeight * (loopsNeeded - 1) : boxHeight,
        String(Math.random()),
        this.leftOfBoreOffset,
        topYOrd + i * boxHeight,
      )
      materialElem.setAttribute('class', materialClass)
      materialElem.appendChild(this.getSVGStyle(materialClass, color, isWaterBearing))
      materialElem.appendChild(material.cloneNode(true))
      yOrd += boxHeight
      parentSvg.appendChild(materialElem)
    }

    return topYOrd + materialHeight
  }

  // render all of the screens
  renderScreens(svgElem: any) {
    for (let i = 0; i < this.configuration.screens.length; i++) {
      this.renderScreen(
        svgElem,
        this.configuration.screens[i]['screenTop'],
        this.configuration.screens[i]['screenBottom'],
      )
    }
  }

  // render static water level
  renderStaticWaterLevel(svgElem: any, staticWaterLevel: number) {
    const waterColor = '#007bba'

    if (staticWaterLevel != null) {
      const scaledStaticWaterLevel: number =
        this.exportHeaderHeight + staticWaterLevel * this.getVerticalScalingFactor()

      // Straight line across the bore
      svgElem.appendChild(
        this.getLine(
          this.boreBottomLineOffset,
          scaledStaticWaterLevel,
          this.leftOfBoreOffset + this.boreWidth,
          scaledStaticWaterLevel,
          `stroke:${waterColor};stroke-width:1;fill:${waterColor}`,
        ),
      )

      // Inverted Triangle
      const newpath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      newpath.setAttributeNS(
        null,
        'd',
        `M ${this.boreBottomLineOffset + 10},${scaledStaticWaterLevel} ${this.boreBottomLineOffset + 20},${
          scaledStaticWaterLevel - this.staticWaterLevelIndicatorHeight
        } ${this.boreBottomLineOffset},${scaledStaticWaterLevel - this.staticWaterLevelIndicatorHeight} z`,
      )

      newpath.setAttributeNS(null, 'style', `fill: ${waterColor}`)

      svgElem.appendChild(newpath)

      // text to left of bore
      const waterText = this.getText(
        this.boreBottomLineOffset - this.boreBottomTextPadding - 15,
        scaledStaticWaterLevel + this.verticallyCenteredTextOffset,
        staticWaterLevel + ' m',
      )
      waterText.setAttributeNS(null, 'style', `fill: ${waterColor}`)

      svgElem.appendChild(waterText)
    }
  }

  // render an individual screen
  renderScreen(svgElem: any, stratumTop: number, stratumBottom: number) {
    const scaledStratumTop: number = stratumTop * this.getVerticalScalingFactor()
    const scaledStratumHeight: number = (stratumBottom - stratumTop) * this.getVerticalScalingFactor()
    const scaledStratumBottom: number = stratumBottom * this.getVerticalScalingFactor()

    // Outer rectangle
    svgElem.appendChild(
      this.getRect(
        this.leftOfBoreOffset - this.screenWidth,
        scaledStratumTop + this.exportHeaderHeight,
        20,
        scaledStratumHeight,
        'stroke:black;stroke-width:1;fill:none',
      ),
    )

    // Vertical lines
    for (let i = 0; i < this.screenWidth / 2; i++) {
      if (i % 2 === 0) {
        svgElem.appendChild(
          this.getLine(
            this.leftOfBoreOffset - this.screenWidth + i * 2,
            scaledStratumTop + this.exportHeaderHeight,
            this.leftOfBoreOffset - this.screenWidth + i * 2,
            scaledStratumBottom + this.exportHeaderHeight,
          ),
        )
      }
    }
  }

  // Render all of the strata, either major, minor or trace
  renderStrata(svgElement: any, primacy: string = 'major') {
    this.accruedDescriptionLabelYOrd = this.exportHeaderHeight
    this.accruedDepthLabelYOrd = this.exportHeaderHeight
    let newTopY = this.exportHeaderHeight
    let stratum
    for (let i = 0; i < this.configuration.strata.length; i++) {
      stratum = this.configuration.strata[i]
      if (i === 0) {
        newTopY = this.exportHeaderHeight + stratum.stratumTop * this.getVerticalScalingFactor()
      }

      if (stratum[primacy + 'FractionId'] != null) {
        newTopY = this.createElementsForStratum(
          stratum['stratumTop'],
          stratum['stratumBottom'],
          stratum['staticWaterLevel'],
          svgElement,
          stratum[primacy + 'FractionId'],
          stratum['stratumDrillersDescription']
            ? stratum['stratumDrillersDescription']
            : stratum['stratumGeneratedDescription'], // SUPNWP-124
          stratum['stratumThickness'] * this.getVerticalScalingFactor(),
          newTopY,
          stratum[primacy + 'FractionColour'],
          stratum['waterBearingNature'] !== 'Unsaturated',
          primacy,
        )

        // Something went wrong, abort rendering
        if (newTopY === -1) {
          return
        }
      } else {
        // No fraction, but we need to accumulate the height for it as if there was
        newTopY = newTopY + stratum.stratumThickness * this.getVerticalScalingFactor()
      }
    }

    // Bottom line for final stratum
    if (primacy === 'major' && stratum && stratum['stratumBottom'] !== this.configuration.header.maxDepthDrilled) {
      if (
        this.accruedDepthLabelYOrd - this.exportHeaderHeight <
        stratum['stratumBottom'] * this.getVerticalScalingFactor()
      ) {
        this.accruedDepthLabelYOrd =
          this.exportHeaderHeight + stratum['stratumBottom'] * this.getVerticalScalingFactor()
      }

      this.renderStratumBottomLine(
        svgElement,
        this.exportHeaderHeight + stratum['stratumBottom'] * this.getVerticalScalingFactor(),
        this.accruedDepthLabelYOrd,
        stratum['stratumBottom'],
      )
    }

    if (primacy === 'major') {
      let finalYOrd =
        this.exportHeaderHeight + this.configuration.header.maxDepthDrilled * this.getVerticalScalingFactor()
      if (
        this.accruedDepthLabelYOrd + this.minLabelHeight >
        this.exportHeaderHeight + this.configuration.header.maxDepthDrilled * this.getVerticalScalingFactor()
      ) {
        finalYOrd = this.accruedDepthLabelYOrd + this.minLabelHeight
      }

      this.renderStratumBottomLine(
        svgElement,
        this.exportHeaderHeight + this.configuration.header.maxDepthDrilled * this.getVerticalScalingFactor(),
        finalYOrd,
        this.configuration.header.maxDepthDrilled,
      )
    }
  }

  renderStratumBottomLine(parentSvg: any, yOrd: number, adjustedYOrd: number, title: string) {
    // (potentially) slanted line to left of bore
    parentSvg.appendChild(this.getLine(this.leftOfBoreOffset, yOrd, this.leftOfBoreOffset - 10, adjustedYOrd))

    // Straight line across the bore
    parentSvg.appendChild(this.getLine(this.leftOfBoreOffset + this.boreWidth, yOrd, this.leftOfBoreOffset, yOrd))

    // Straight line to the left of bore
    parentSvg.appendChild(
      this.getLine(this.boreBottomLineOffset, adjustedYOrd, this.leftOfBoreOffset - 10, adjustedYOrd),
    )

    // text to left of bore
    if (yOrd !== 0) {
      parentSvg.appendChild(
        this.getText(
          this.boreBottomLineOffset - this.boreBottomTextPadding - 15,
          adjustedYOrd + this.verticallyCenteredTextOffset,
          title + ' m',
        ),
      )
    }
  }

  // render the strata title - Note this and the line will have to handle situation of small strata using angled lines.
  // See MDC visualations for example
  renderTitle(parentSvg: any, yOrd: number, adjustedYOrd: number, title: string) {
    // (potentially) slanted line to right of bore
    parentSvg.appendChild(
      this.getLine(
        this.boreWidth + this.leftOfBoreOffset,
        yOrd,
        this.boreWidth + this.leftOfBoreOffset + 10,
        adjustedYOrd,
      ),
    )

    // Straight line to right of bore
    parentSvg.appendChild(
      this.getLine(
        this.boreWidth + this.leftOfBoreOffset + 10,
        adjustedYOrd,
        this.boreWidth + this.leftOfBoreOffset + this.totalVisualisationWidth,
        adjustedYOrd,
      ),
    )

    // text to right of bore
    return this.appendMultilineText(
      parentSvg,
      this.boreWidth + this.leftOfBoreOffset + 20,
      adjustedYOrd,
      this.stratumDescriptionWidth,
      title,
    )
  }

  // render the constraining lines above and below the bore
  renderVisualisationBoundaries(parentSvg: any) {
    parentSvg.appendChild(
      this.getLine(0, this.exportHeaderHeight, this.totalVisualisationWidth, this.exportHeaderHeight),
    )
  }

  // render the constraining lines around the bore
  renderBoreConstraints(parentSvg: any) {
    const totalHeight: number = this.getTotalHeight()
    parentSvg.appendChild(
      this.getLine(
        this.leftOfBoreOffset,
        0 + this.exportHeaderHeight,
        this.leftOfBoreOffset,
        totalHeight + this.exportHeaderHeight,
      ),
    )
    parentSvg.appendChild(
      this.getLine(
        this.leftOfBoreOffset + this.boreWidth,
        0 + this.exportHeaderHeight,
        this.leftOfBoreOffset + this.boreWidth,
        totalHeight + this.exportHeaderHeight,
      ),
    )
  }

  // Render a scale bar on the left of the visualisation
  renderScale(parentSvg: any) {
    parentSvg.appendChild(
      this.getLine(
        this.boreScaleLeftOffset,
        this.exportHeaderHeight,
        this.boreScaleLeftOffset,
        this.getTotalHeight() + this.exportHeaderHeight,
      ),
    )
    parentSvg.appendChild(
      this.getLine(
        this.boreScaleLeftOffset + this.boreScaleWidth,
        0 + this.exportHeaderHeight,
        this.boreScaleLeftOffset + this.boreScaleWidth,
        this.getTotalHeight() + this.exportHeaderHeight,
      ),
    )

    let styleSwapCount = 0
    let style = 'lightgray'
    for (let i = 0; i < this.configuration.header.maxDepthDrilled + 1; i++) {
      styleSwapCount++
      let height = this.getVerticalScalingFactor()
      if (i * this.getVerticalScalingFactor() + this.getVerticalScalingFactor() > this.getTotalHeight()) {
        height = this.getTotalHeight() - i * this.getVerticalScalingFactor()
      }

      parentSvg.appendChild(
        this.getRect(
          this.boreScaleLeftOffset,
          this.exportHeaderHeight + i * this.getVerticalScalingFactor(),
          this.boreScaleWidth,
          height,
          'stroke:black;stroke-width:1;fill:' + style,
        ),
      )

      if (styleSwapCount === 5) {
        styleSwapCount = 0
        style = style === 'white' ? 'lightgrey' : 'white'
      }

      if (i === 0) {
        parentSvg.appendChild(
          this.getText(this.boreScaleLeftOffset - this.boreScaleLeftTextOffset, 15 + this.exportHeaderHeight, '0 m'),
        )
      } else if (i % 5 === 0) {
        parentSvg.appendChild(
          this.getText(
            this.boreScaleLeftOffset - this.boreScaleLeftTextOffset,
            i * this.getVerticalScalingFactor() + this.verticallyCenteredTextOffset + this.exportHeaderHeight,
            i + ' m',
          ),
        )
      } else if (i === Math.floor(this.configuration.header.maxDepthDrilled)) {
        parentSvg.appendChild(
          this.getText(
            this.boreScaleLeftOffset - this.boreScaleLeftTextOffset,
            i * this.getVerticalScalingFactor() + this.verticallyCenteredTextOffset + this.exportHeaderHeight,
            i + ' m',
          ),
        )
      }
    }
  }

  // render the entire visualisation
  render() {
    const boreHeight: number = this.getTotalHeight()
    const svgElement: any = this.getSVG(
      this.boreWidth + this.leftOfBoreOffset + this.totalVisualisationWidth,
      boreHeight + this.bottomOfBorePadding + this.exportHeaderHeight,
      'borelog_svg',
    )
    const svgContainer: any = document.getElementById(this.containerName)
    // If there is already something half-rendered hanging off here, we need to remove it
    svgContainer.innerHTML = ''
    svgContainer.appendChild(svgElement)
    this.renderStrata(svgElement, 'some')
    this.renderStrata(svgElement, 'secondary')
    this.renderStrata(svgElement, 'major')
    this.renderScreens(svgElement)
    this.renderBoreConstraints(svgElement)
    this.renderVisualisationBoundaries(svgElement)
    this.renderScale(svgElement)
    this.renderHeader(svgElement)
  }
}
